import {
  formatCompactNumber,
  formatCurrency,
  formatNumber,
  formatPercent,
} from "../../charts/formatters"

export { formatCompactNumber, formatCurrency, formatNumber, formatPercent }

const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "X (Twitter)",
  tiktok: "TikTok",
  all: "All platforms",
}

export function formatPlatformLabel(platform) {
  if (!platform) return "—"
  const key = String(platform).toLowerCase()
  return PLATFORM_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1)
}

export function formatReportDate(value) {
  if (!value) return "—"
  const raw = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number)
    if (year && month && day) {
      return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    }
  }
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

export function formatReportHour(value) {
  if (value == null || value === "") return "—"
  const h = Number(value)
  if (!Number.isFinite(h)) return String(value)
  const period = h >= 12 ? "PM" : "AM"
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:00 ${period}`
}

const GOAL_STATUS_LABELS = {
  "on-track": "On track",
  behind: "Behind",
  "needs-attention": "Needs Attention",
  "at-risk": "At risk",
  exceeded: "Achieved",
  achieved: "Achieved",
}

export function formatGoalProgressStatus(status) {
  if (!status) return "—"
  const key = String(status).trim().toLowerCase()
  return GOAL_STATUS_LABELS[key] ?? String(status)
}

export function formatGoalPeriodLabel(period) {
  if (!period) return "—"
  const key = String(period).trim().toLowerCase()
  switch (key) {
    case "monthly":
    case "month":
      return "Monthly"
    case "quarterly":
    case "quarter":
      return "Quarterly"
    case "annual":
    case "yearly":
    case "year":
      return "Annual"
    default:
      return key.charAt(0).toUpperCase() + key.slice(1)
  }
}

export function formatExportGoalValue(value, format) {
  if (value == null || value === "") return "—"
  switch (format) {
    case "currency":
      return `$${formatCompactNumber(value)}`
    case "percent":
      return `${value}%`
    case "ratio":
      return `${Number(value).toFixed(1)}x`
    case "time":
      return `${value} min`
    case "timeSeconds":
      return `${value} sec`
    case "stars":
      return `${Number(value).toFixed(1)} stars`
    default:
      return formatCompactNumber(value)
  }
}

export function formatCellByType(value, type) {
  if (value == null || value === "") return "—"
  switch (type) {
    case "date":
      return formatReportDate(value)
    case "currency":
      return formatCurrency(value)
    case "compact":
      return formatCompactNumber(value)
    case "number":
      return formatNumber(value)
    case "percent":
      return formatPercent(value, 1)
    case "percentRaw":
      return formatPercent(value, 1)
    case "metricDelta":
      if (value && typeof value === "object") {
        const main = value.value ?? "—"
        return value.delta ? `${main} (${value.delta})` : main
      }
      return String(value ?? "—")
    case "platform":
      return formatPlatformLabel(value)
    case "platformIcon":
      return formatPlatformLabel(value)
    case "competitor":
      if (value && typeof value === "object") return value.name ?? "—"
      return String(value ?? "—")
    case "postCaption": {
      if (value && typeof value === "object") {
        const title = String(value.title ?? "").trim()
        const copy = String(value.copy ?? "").trim()
        if (title && copy) return `${title} — ${copy}`
        return title || copy || "—"
      }
      return String(value ?? "—")
    }
    case "growth":
      return String(value ?? "—")
    case "link":
      if (value && typeof value === "object") {
        return value.label || (value.url ? "View post" : "—")
      }
      return value ? "View post" : "—"
    case "hour":
      return formatReportHour(value)
    case "goalStatus":
      return formatGoalProgressStatus(value)
    default:
      return String(value)
  }
}

export function formatTableRow(row, columnTypes = []) {
  return row.map((cell, index) => formatCellByType(cell, columnTypes[index] || "text"))
}

/** Keep time-series tables readable in reports. */
export function sliceSeriesForReport(series, maxPoints = 14) {
  if (!Array.isArray(series)) return []
  if (series.length <= maxPoints) return series
  return series.slice(-maxPoints)
}

export function sumField(rows, field) {
  return (rows || []).reduce((sum, row) => sum + (Number(row?.[field]) || 0), 0)
}
